import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import type { PersonResponse } from '../types/api';

/**
 * Hook for checking person-level permissions based on user role and organization access.
 *
 * Permission Logic:
 * - superAdmin: Can edit any person
 * - organizationAdmin: Can edit persons who have assignments to their organization(s)
 * - viewer: Read-only access
 */
export const usePersonPermissions = (person: PersonResponse | null) => {
  const { currentUser } = useAuth();

  const canEditBasicInfo = useMemo(() => {
    if (!currentUser || !person) return false;

    // SuperAdmin can edit anyone
    if (currentUser.role === 'superAdmin') return true;

    // OrganizationAdmin can edit if person has assignment to any of their orgs
    // For schools, also check if the parent LEA's educationOrganizationId is in their org list
    if (currentUser.role === 'organizationAdmin') {
      const userOrgIds = currentUser.organizationIds || [];
      return person.roles.some(role =>
        userOrgIds.includes(role.educationOrganizationId) ||
        (role.parentLeaEducationOrganizationId && userOrgIds.includes(role.parentLeaEducationOrganizationId))
      );
    }

    return false;
  }, [currentUser, person]);

  const canManageRolesForOrg = useMemo(() => {
    /**
     * Check if user can manage roles for an organization.
     * For schools, pass the parent LEA's educationOrganizationId to check against user's org access.
     */
    return (organizationId: number, parentLeaEducationOrganizationId?: number | null) => {
      if (!currentUser) return false;

      // SuperAdmin can manage all
      if (currentUser.role === 'superAdmin') return true;

      // OrganizationAdmin can manage for their orgs only
      // For schools, check if they have access to the parent LEA
      if (currentUser.role === 'organizationAdmin') {
        const userOrgIds = currentUser.organizationIds || [];
        return userOrgIds.includes(organizationId) ||
               (parentLeaEducationOrganizationId != null && userOrgIds.includes(parentLeaEducationOrganizationId));
      }

      return false;
    };
  }, [currentUser]);

  const canViewPerson = useMemo(() => {
    // All authenticated users can view person profiles
    return currentUser !== null;
  }, [currentUser]);

  const canViewAddresses = useMemo(() => {
    // SuperAdmin can always view addresses
    if (currentUser?.role === 'superAdmin') return true;
    // OrgAdmin can view addresses if they can edit basic info (person at their org)
    if (currentUser?.role === 'organizationAdmin') return canEditBasicInfo;
    return false;
  }, [currentUser, canEditBasicInfo]);

  const isSuperAdmin = useMemo(() => {
    return currentUser?.role === 'superAdmin';
  }, [currentUser]);

  const isOrgAdmin = useMemo(() => {
    return currentUser?.role === 'organizationAdmin';
  }, [currentUser]);

  return {
    canEditBasicInfo,
    canViewAddresses,
    canManageRolesForOrg,
    canViewPerson,
    isSuperAdmin,
    isOrgAdmin,
    currentUser
  };
};